export enum FormField {
  Password = `password`,
  Confirm = `confirm`,
}

export enum PasswordLevel {
  Basic = 0,
  Heightened = 1,
}

export interface FormValues {
  [FormField.Password]: string;
  [FormField.Confirm]: string;
}

type ValidationError = string | null;

export interface FormErrors {
  [FormField.Password]: ValidationError;
  [FormField.Confirm]: ValidationError;
}

function passwordsMatch(input: FormValues): ValidationError {
  if (input.password !== input.confirm) {
    return `Passwords do not match.`;
  }
  return null;
}

function passwordLength(input: FormValues): ValidationError {
  if (input.password.length < 8 || input.password.length > 64) {
    return `Your new password must be at least 8 characters and less than 64 characters.`;
  }
  return null;
}

function passwordUpperAndLowerCase(input: FormValues): ValidationError {
  if (!/[a-z]/.test(input.password) || !/[A-Z]/.test(input.password)) {
    return `Your new password needs an uppercase and lowercase letter.`;
  }
  return null;
}

function passwordHasNumber(input: FormValues): ValidationError {
  if (!/\d/.test(input.password)) {
    return `Your new password needs at least one number.`;
  }
  return null;
}

type Validator = (input: FormValues) => ValidationError;
type ValidationRules = {[field in FormField]: Validator[]};

export const basicValidationRules: ValidationRules = {
  [FormField.Password]: [passwordLength],
  [FormField.Confirm]: [passwordsMatch],
};

export const heightenedValidationRules: ValidationRules = {
  [FormField.Password]: [passwordLength, passwordUpperAndLowerCase, passwordHasNumber],
  [FormField.Confirm]: [passwordsMatch],
};

function getValidationError(field: FormField, formValues: FormValues, passwordLevel: number): ValidationError {
  const validators = passwordLevel > 0 ? heightenedValidationRules : basicValidationRules;
  for (const fn of validators[field]) {
    const error = fn(formValues);
    if (error) {
      return error;
    }
  }
  return null;
}

export function getAllValidationErrors(formValues: FormValues, passwordLevel: PasswordLevel): FormErrors {
  return {
    [FormField.Password]: getValidationError(FormField.Password, formValues, passwordLevel),
    [FormField.Confirm]: getValidationError(FormField.Confirm, formValues, passwordLevel),
  };
}
